import { useEffect } from "react";
import { Message } from "@/types";
import { getUserFriendlyErrorMessage } from "@/utils/errorMessages";
import { useFeatureFlags } from "@/utils/FeatureFlagDetection";
import { syncChevronTurn } from "./syncChevronTurn";
import {
  ExecutionStatus,
  ExecutionState,
  isExecutionActive,
  resetExecution,
} from "../../../../redux/reducers/Execution";
import { AppDispatch } from "../../../../redux/store";
import { ChatStreamingRefs } from "./useChatStreamingRefs";

interface UseExecutionMessageSyncOptions {
  executionState: ExecutionState;
  currentConversationId: string | null;
  refs: ChatStreamingRefs;
  addMessage: (conversationId: string, message: Omit<Message, "id">) => string;
  updateMessage: (
    conversationId: string,
    messageId: string,
    updates: Partial<Message>,
  ) => void;
  dispatch: AppDispatch;
}

const DUPLICATE_REQUEST_MESSAGE =
  "A request is already being processed for this project. Please wait for it to complete.";

/**
 * Watches the Redux Execution slice (updated by WebSocket messages from the
 * backend) and synchronizes its streaming/segment/completion state into the
 * current chat conversation.
 *
 * Responsibilities:
 *  - Finalize completed inter-event segments (snapshots of streamingContent
 *    captured by the reducer at system-event boundaries) into chat messages.
 *  - Lazily create a placeholder AI message when streamingContent resumes
 *    after a segment boundary.
 *  - Update the active placeholder with incoming streamingContent.
 *  - Finalize the message and reset refs on COMPLETED / FAILED / DUPLICATE.
 *  - Provide an IDLE safety net for missed transitions due to React 18
 *    automatic batching.
 */
export function useExecutionMessageSync({
  executionState,
  currentConversationId,
  refs,
  addMessage,
  updateMessage,
  dispatch,
}: UseExecutionMessageSyncOptions): void {
  const { isThinkingChevronEnabled } = useFeatureFlags(["thinkingChevron"]);

  useEffect(() => {
    if (!currentConversationId) return;

    const {
      status,
      streamingContent,
      completedSegments,
      errorCode,
      errorMessage,
    } = executionState;

    const convId = currentConversationId;
    const { streamingMessageIdRef, processedSegmentsRef } = refs;

    // `thinkingChevron`: the whole turn is one message carrying an ordered
    // text + tool-call timeline, so tool chevrons render inline between text
    // rather than the turn being split into a bubble per tool boundary.
    if (isThinkingChevronEnabled) {
      syncChevronTurn({
        executionState,
        conversationId: convId,
        refs,
        addMessage,
        updateMessage,
        dispatch,
      });
      return;
    }

    // --- Process any new completed segments (inter-event text chunks) ---
    // Each segment finalizes the current streaming message. We do NOT
    // eagerly create a new placeholder here — it's deferred until new
    // streamingContent actually arrives. This prevents empty chat bubbles
    // when the last event before completion is a system event with no
    // subsequent text.
    if (completedSegments.length > processedSegmentsRef.current) {
      for (
        let i = processedSegmentsRef.current;
        i < completedSegments.length;
        i++
      ) {
        const segmentContent = completedSegments[i];
        const currentMsgId = streamingMessageIdRef.current;
        if (currentMsgId) {
          // Finalize the current message with the segment's content
          updateMessage(convId, currentMsgId, {
            content: segmentContent,
            isLoading: false,
          });
        }

        // Clear the ref — a new placeholder will be created lazily
        // when streamingContent starts arriving again.
        streamingMessageIdRef.current = null;
      }
      processedSegmentsRef.current = completedSegments.length;
    }

    // Lazily create a placeholder when streaming resumes after a segment boundary.
    // The isExecutionActive guard prevents duplicate placeholders between
    // COMPLETED and resetExecution(), when streamingContent is still populated.
    if (
      streamingContent &&
      !streamingMessageIdRef.current &&
      isExecutionActive(status)
    ) {
      const newMsgId = addMessage(convId, {
        content: "",
        isUser: false,
        isLoading: true,
      });
      streamingMessageIdRef.current = newMsgId;
    }

    const msgId = streamingMessageIdRef.current;

    // Update the AI message with streaming content
    if (streamingContent && msgId) {
      updateMessage(convId, msgId, {
        content: streamingContent,
        isLoading: isExecutionActive(status),
      });
    }

    // Execution completed — the handleExecutionCompleted thunk handles
    // project metadata refresh, asset invalidation, and resetExecution().
    // We just need to finalize the chat message here.
    if (status === ExecutionStatus.COMPLETED) {
      if (msgId) {
        updateMessage(convId, msgId, {
          isLoading: false,
        });
      }
      streamingMessageIdRef.current = null;
      processedSegmentsRef.current = 0;
    }

    // Execution failed — map error codes to user-friendly messages
    if (status === ExecutionStatus.FAILED) {
      const errorContent = getUserFriendlyErrorMessage(errorCode, errorMessage);
      if (msgId) {
        updateMessage(convId, msgId, {
          content: errorContent,
          isLoading: false,
        });
      }
      streamingMessageIdRef.current = null;
      processedSegmentsRef.current = 0;
      dispatch(resetExecution());
    }

    // Duplicate request
    if (status === ExecutionStatus.DUPLICATE) {
      if (msgId) {
        updateMessage(convId, msgId, {
          content: DUPLICATE_REQUEST_MESSAGE,
          isLoading: false,
        });
      }
      streamingMessageIdRef.current = null;
      processedSegmentsRef.current = 0;
      dispatch(resetExecution());
    }

    // Safety net: if execution returned to IDLE while we still have a streaming
    // message ref, it means we missed the COMPLETED transition (can happen with
    // React 18 automatic batching when setCompleted and resetExecution are
    // dispatched in quick succession). Finalize the message here.
    if (status === ExecutionStatus.IDLE && streamingMessageIdRef.current) {
      updateMessage(convId, streamingMessageIdRef.current, {
        isLoading: false,
      });
      streamingMessageIdRef.current = null;
      processedSegmentsRef.current = 0;
    }
  }, [
    executionState,
    currentConversationId,
    updateMessage,
    addMessage,
    dispatch,
    refs,
    isThinkingChevronEnabled,
  ]);
}
